import { useEffect, useState } from "react";
import { Package, ShoppingBag, DollarSign, AlertTriangle, Users, TrendingUp } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { fmtINR, fmtUSD } from "@/lib/money";

export default function AdminDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [m, o] = await Promise.all([
          api.get("/admin/metrics"),
          api.get("/orders/all"),
        ]);
        setMetrics(m.data);
        setOrders(o.data.slice(0, 5));
      } catch {}
    })();
  }, []);

  if (!metrics) return <div className="font-display text-xl text-[#4A148C]">Loading metrics…</div>;

  const cards = [
    { label: "Revenue", value: `₹${fmtINR(metrics.revenue)}`, sub: `≈ $${fmtUSD(metrics.revenue)} USD`, icon: DollarSign, tint: "bg-[#E65C00]" },
    { label: "Total Orders", value: metrics.total_orders, icon: ShoppingBag, tint: "bg-[#4A148C]" },
    { label: "Products", value: `${metrics.active_products}/${metrics.total_products}`, icon: Package, tint: "bg-[#FFB300]" },
    { label: "Customers", value: metrics.total_customers, icon: Users, tint: "bg-[#0F7B3B]" },
    { label: "Low Stock", value: metrics.low_stock, icon: AlertTriangle, tint: "bg-[#B71C1C]" },
    { label: "Growth", value: "▲ 12%", icon: TrendingUp, tint: "bg-[#6A2AB0]" },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-4xl text-[#2C1810]">Dashboard</h1>
        <p className="font-body text-[#5B3D2A]">A quick pulse of your Mona&apos;s Creations storefront.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={`metric-${c.label}`} className="bg-white rounded-2xl border border-[#FFD1A6] p-5 shadow-warm">
            <div className={`w-9 h-9 rounded-full ${c.tint} text-white flex items-center justify-center`}>
              <c.icon className="w-4 h-4" />
            </div>
            <div className="mt-3 text-[10px] uppercase tracking-widest text-[#4A148C] font-body">{c.label}</div>
            <div className="font-display text-2xl text-[#2C1810]">{c.value}</div>
            {c.sub && <div className="text-[10px] font-body text-[#5B3D2A]/70 mt-0.5">{c.sub}</div>}
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-[#FFD1A6] p-6 shadow-warm">
        <div className="font-display text-2xl text-[#2C1810] mb-4">Recent orders</div>
        {orders.length === 0 ? (
          <div className="font-body text-sm text-[#5B3D2A]">No orders yet.</div>
        ) : (
          <div className="divide-y divide-[#FFD1A6]">
            {orders.map((o) => (
              <div key={o.id} className="py-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-body text-sm font-semibold text-[#2C1810]">#{o.id.slice(0, 8)}</div>
                  <div className="text-xs font-body text-[#5B3D2A]">{o.user_email} · {new Date(o.created_at).toLocaleDateString()}</div>
                </div>
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#FFF3E0] text-[#E65C00]">{o.status}</span>
                <div className="text-right">
                  <div className="font-display text-lg text-[#4A148C] font-semibold">₹{fmtINR(o.total)}</div>
                  <div className="text-[10px] font-body text-[#5B3D2A]/70">≈ ${fmtUSD(o.total)} USD</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
